import { ErrorLevel } from '@griply/utils';
import { FirebaseApp } from 'firebase/app';
import {
  AppCheck,
  getToken,
  initializeAppCheck,
  onTokenChanged,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { getErrorReporter } from 'shared/services/error-tracking';

let appCheck: AppCheck | undefined;
let callInitialized = false;
let onlineListener: (() => void) | undefined;
let placeholderReported = false;

const removeOnlineListener = () => {
  if (!onlineListener) {
    return;
  }

  window.removeEventListener('online', onlineListener);
  onlineListener = undefined;
};

// Real App Check tokens are JWTs; the SDK hands out a single-segment placeholder when it cannot mint one.
const isPlaceholderToken = (token: string) => !token.split('.')[2];

// The placeholder is attached to requests like a real token, so every call it rides on is rejected server-side.
const watchForPlaceholderTokens = (instance: AppCheck) => {
  onTokenChanged(instance, ({ token }) => {
    if (placeholderReported || !isPlaceholderToken(token)) {
      return;
    }

    placeholderReported = true;

    getErrorReporter().captureMessage(
      'App Check served a placeholder token',
      ErrorLevel.Warning,
      { tags: { source: 'app-check-placeholder-token' } },
    );
  });
};

// A 403/404 from the token exchange throttles the provider for 24h in memory — surface it at startup.
const probeToken = async (instance: AppCheck) => {
  try {
    await getToken(instance);
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'app-check-probe' },
      extra: { code: (error as { code?: string }).code },
    });
  }
};

const initializeAppCheckWhenOnline = (app: FirebaseApp) => {
  if (callInitialized) {
    return;
  }

  if (!navigator.onLine) {
    removeOnlineListener();
    onlineListener = () => {
      initializeAppCheckWhenOnline(app);
    };
    window.addEventListener('online', onlineListener, { once: true });
    return;
  }

  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-restricted-globals -- Debug is only turned on locally, which is needed to make it work on localhost
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(
        import.meta.env.VITE_FIREBASE_RECAPTCHA_KEY,
      ),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // Leaving callInitialized false lets a later call retry instead of stranding the session tokenless.
    getErrorReporter().captureException(error, {
      tags: { source: 'app-check-init' },
    });
    return;
  }

  callInitialized = true;
  removeOnlineListener();
  watchForPlaceholderTokens(appCheck);
  void probeToken(appCheck);
};

export const initAppCheck = (app: FirebaseApp) => {
  if (appCheck) {
    return appCheck;
  }

  initializeAppCheckWhenOnline(app);

  return appCheck;
};

export const clearAppCheck = () => {
  appCheck = undefined;
  callInitialized = false;
  placeholderReported = false;
  removeOnlineListener();
};
